import React, { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil } from "lucide-react";

const ROLES = ["super_admin", "admin", "branch_head", "cashier", "warehouse", "reseller", "financial"];
const empty = () => ({ name: "", email: "", password: "", role: "cashier", branch_id: "", active: true });

export default function Users() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const canManage = ["super_admin", "admin"].includes(user.role);
  const load = () => client.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "Semua";
  const allBranchRole = ["super_admin", "admin", "financial"].includes(form.role);

  const save = async () => {
    try {
      const payload = { ...form, branch_id: allBranchRole ? null : form.branch_id || null };
      if (editId) await client.put(`/users/${editId}`, payload);
      else await client.post("/users", payload);
      toast.success("Pengguna disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pengguna</h1>
          <p className="text-sm text-muted-foreground">Kelola staf & peran akses</p>
        </div>
        {canManage && <Button onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }} data-testid="add-user-btn"><Plus className="mr-2 h-4 w-4" /> Tambah</Button>}
      </div>
      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead>Cabang</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} className="hover:bg-slate-50">
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="secondary">{ROLE_LABELS[u.role]}</Badge></TableCell>
                <TableCell>{u.role === "admin" ? "Region (cabang sendiri)" : u.role === "super_admin" ? "Semua" : u.role === "financial" ? "Semua (read-only)" : branchName(u.branch_id)}</TableCell>
                <TableCell>{u.active !== false ? <Badge className="bg-emerald-100 text-emerald-700">aktif</Badge> : <Badge variant="secondary">nonaktif</Badge>}</TableCell>
                <TableCell>{canManage && <Button size="icon" variant="ghost" onClick={() => { setForm({ name: u.name, email: u.email, password: "", role: u.role, branch_id: u.branch_id || "", active: u.active !== false }); setEditId(u.id); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} data-testid="user-email" /></div>
            <div className="space-y-2"><Label>Kata Sandi {editId && "(kosongkan jika tetap)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div className="space-y-2"><Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!allBranchRole && (
              <div className="space-y-2"><Label>Cabang</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="user-branch"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="save-user-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
